// eslint-disable-next-line max-classes-per-file
import React, {
    ForwardedRef,
    ReactNode,
    RefObject,
    useEffect,
    useMemo,
} from "react";
import { Permission } from "../../../__generated__/api";
import useCurrentSession from "../../../contexts/session";
import createId from "../../../create-id";
import useOrCreateRef from "../../useOrCreateRef";
import EventHandler from "../../../event-handler";

export enum Events {
    UPDATED_SELECTED_COLUMN = "UPDATED_SELECTED_COLUMN",
    SETTING_CHANGED = "SETTING_CHANGED",
    SECTION_LOADED = "SECTION_LOADED"
}

export class Setting {
    public constructor(
        private reference: RefObject<HTMLDivElement>,
        public readonly canDisplay: boolean,
        public readonly title: string,
        public readonly description?: string,
    ) {
        // nothing
    }
}

export class SettingSectionRegistry {
    private registry: Setting[] = [];

    public readonly id: string;

    public constructor(
        public readonly reference: RefObject<HTMLDivElement>,
        public readonly container: SettingsContainerRegistry,
        public readonly title: ReactNode,
        public readonly helpText?: string,
        public readonly sectionKey?: string,
        public counter?: string,
    ) {
        this.id = createId();
    }

    public scrollIntoView() {
        this.reference.current?.scrollIntoView({ behavior: "smooth" });
    }

    public get canDisplay() {
        if (this.container.selectedTab != null) {
            return this.container.selectedTab === this;
        }
        return this.registry.some(section => section.canDisplay);
    }

    public useSetting(
        inputRef: ForwardedRef<HTMLDivElement>,
        options: {
            permissions: Permission[],
            title: string,
            description?: string,
            isVisible?: boolean
        },
    ) {
        const {
            permissions,
            title,
            description,
            isVisible,
        } = options;

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const generation = this.container.settingsRegistry.events.useEvent(Events.UPDATED_SELECTED_COLUMN);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const ref = useOrCreateRef<HTMLDivElement>(inputRef);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const { item, canDisplay } = useMemo(() => {
            const shouldDisplay = isVisible !== false && (this.container.selectedTab ? this.container.selectedTab === this : true)
                && (permissions.isEmpty
                || permissions.some(permission => this.container.settingsRegistry.permissions.has(permission)));

            return {
                item: new Setting(ref, shouldDisplay, title, description),
                canDisplay: shouldDisplay,
            };
        }, [title, description, this, this.container, this.registry, generation, this.container.selectedTab, isVisible]);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            this.registry.push(item);
            this.container.settingsRegistry.events.emit(Events.SETTING_CHANGED);
            return () => {
                this.registry = this.registry.filter(row => row !== item);
                this.container.settingsRegistry.events.emit(Events.SETTING_CHANGED);
            };
        }, [this, this.container, item, canDisplay]);

        return {
            reference: ref,
            canDisplay,
        };
    }
}

export class SettingsContainerRegistry {
    private registry: SettingSectionRegistry[] = [];

    private _selectedTab: SettingSectionRegistry | undefined;

    public constructor(public readonly settingsRegistry: SettingsRegistry) {
        //
    }

    public get selectedTab() {
        return this._selectedTab;
    }

    public get sections() {
        return this.registry;
    }

    public useSection(
        inputRef: ForwardedRef<HTMLDivElement>,
        title: ReactNode,
        helpText?: string,
        sectionKey?: string,
        counter?: string,
    ) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        this.settingsRegistry.events.useEvent(Events.UPDATED_SELECTED_COLUMN);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const ref = useOrCreateRef<HTMLDivElement>(inputRef);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const item = useMemo(
            () => new SettingSectionRegistry(ref, this, title, helpText, sectionKey),
            [title, helpText, this, this.settingsRegistry],
        );
        item.counter = counter;

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            this.registry.push(item);
            this.settingsRegistry.events.emit(Events.SECTION_LOADED);
            return () => {
                this.registry = this.registry.filter(row => row !== item);
                this.settingsRegistry.events.emit(Events.SECTION_LOADED);
            };
        }, [this, this.settingsRegistry, item]);

        return { section: item, reference: ref };
    }

    public useTabSelection(): [SettingSectionRegistry | undefined, (section: SettingSectionRegistry) => void] {
        this.settingsRegistry.events.useEvent(Events.UPDATED_SELECTED_COLUMN);
        return [
            this.selectedTab,
            (section: SettingSectionRegistry) => {
                this._selectedTab = section;
                this.settingsRegistry.events.emit(Events.UPDATED_SELECTED_COLUMN);
            },
        ];
    }
}

class SettingsRegistry {
    public constructor(public readonly permissions: Set<Permission>) {
        //
    }

    private registry: SettingsContainerRegistry[] = [];

    public readonly events = new EventHandler<Events>();

    public useContainer() {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const item = useMemo(() => new SettingsContainerRegistry(this), [this]);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        this.events.useEvent(Events.SETTING_CHANGED);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            this.registry.push(item);
            return () => {
                this.registry = this.registry.filter(row => row !== item);
            };
        }, [this, item]);

        return item;
    }
}

const DEFAULT_REGISTRY = new SettingsRegistry(new Set());
export const SettingsContext = React.createContext<SettingsRegistry>(DEFAULT_REGISTRY);

const DEFAULT_CONTAINER_REGISTRY = new SettingsContainerRegistry(DEFAULT_REGISTRY);
export const SettingsContainerContext = React.createContext<SettingsContainerRegistry>(DEFAULT_CONTAINER_REGISTRY);

const DEFAULT_SECTION_REGISTRY = new SettingSectionRegistry({ current: null }, DEFAULT_CONTAINER_REGISTRY, "");
export const SettingsSectionContext = React.createContext<SettingSectionRegistry>(DEFAULT_SECTION_REGISTRY);

export function SettingRegistryProvider({ children }: {children: ReactNode}): (ReactNode | null) {
    const session = useCurrentSession();
    const instance = useMemo(() => new SettingsRegistry(session.permissions), [session.permissions]);
    return (
        <SettingsContext.Provider value={instance}>
            {children}
        </SettingsContext.Provider>
    );
}
